import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { alertNotificationService } from '../utils/alertNotificationService';
import { showToast } from '../utils/toast';
import Icon from './AppIcon';
import { useNavigate } from 'react-router-dom';

const AlertDropdown = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (userProfile?.role === 'admin') {
      loadAlerts();
      loadUnreadCount();
      subscribeToAlerts();
    }

    return () => {
      if (subscription) {
        alertNotificationService?.unsubscribeFromAlerts(subscription);
      }
    };
  }, [userProfile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('admin_alerts')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (error) throw error;
      setAlerts(data || []);
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const result = await alertNotificationService?.getUnreadCount();
      if (result?.success) {
        setUnreadCount(result?.count);
      }
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const subscribeToAlerts = () => {
    const sub = alertNotificationService?.subscribeToAlerts((newAlert) => {
      setAlerts((prev) => [newAlert, ...prev?.slice(0, 9)]);
      setUnreadCount((prev) => prev + 1);
      showToast(newAlert?.title, 'info');

      // Play sound for critical alerts if enabled
      if (newAlert?.severity === 'critical') {
        playNotificationSound();
      }
    });
    setSubscription(sub);
  };

  const playNotificationSound = () => {
    const audio = new Audio('/notification.mp3');
    audio?.play()?.catch(() => {
      // Ignore errors if sound file not found
    });
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      const result = await alertNotificationService?.markAlertRead(alertId);
      if (result?.success) {
        setAlerts((prev) =>
          prev?.map((alert) => (alert?.id === alertId ? { ...alert, is_read: true } : alert))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const result = await alertNotificationService?.markAllAlertsRead();
      if (result?.success) {
        setAlerts((prev) => prev?.map((alert) => ({ ...alert, is_read: true })));
        setUnreadCount(0);
        showToast('All alerts marked as read', 'success');
      }
    } catch (error) {
      console.error('Error marking all alerts as read:', error);
      showToast('Failed to mark alerts as read', 'error');
    }
  };

  const getAlertIcon = (alertType) => {
    switch (alertType) {
      case 'new_flagged_content':
        return 'Flag';
      case 'new_user_registration':
        return 'UserPlus';
      case 'suspicious_activity':
        return 'AlertTriangle';
      case 'system_error':
        return 'XCircle';
      case 'high_value_transaction':
        return 'DollarSign';
      case 'storage_warning':
        return 'HardDrive';
      default:
        return 'Bell';
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'critical':
        return '#ef4444';
      case 'warning':
        return '#f59e0b';
      case 'info':
      default:
        return '#3b82f6';
    }
  };

  const getSeverityBadgeColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-destructive/10 text-destructive';
      case 'warning':
        return 'bg-warning/10 text-warning';
      case 'info':
      default:
        return 'bg-primary/10 text-primary';
    }
  };

  const handleQuickAction = (alert) => {
    if (alert?.alert_type === 'new_flagged_content') {
      navigate('/admin-dashboard');
      setIsOpen(false);
    } else if (alert?.alert_type === 'new_user_registration') {
      navigate('/admin-dashboard');
      setIsOpen(false);
    }
  };

  if (userProfile?.role !== 'admin') {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg transition-colors duration-250 hover:bg-[#9B8DC7]/10"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={24} color="#9B8DC7" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-5 h-5 bg-destructive text-destructive-foreground text-xs font-semibold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-card border border-border rounded-lg shadow-xl z-50 max-h-[600px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="text-lg font-heading font-semibold text-foreground">Alerts</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs font-interface text-primary hover:underline"
                >
                  Mark all as read
                </button>
              )}
              <button
                onClick={() => navigate('/admin-alerts')}
                className="text-xs font-interface text-primary hover:underline"
              >
                View All
              </button>
            </div>
          </div>

          {/* Alerts List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : alerts?.length === 0 ? (
              <div className="text-center py-8 px-4">
                <Icon name="Bell" size={48} color="var(--color-muted-foreground)" />
                <p className="text-sm font-interface text-muted-foreground mt-2">
                  No alerts yet
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {alerts?.map((alert) => (
                  <div
                    key={alert?.id}
                    className={`p-4 hover:bg-muted/50 transition-colors cursor-pointer ${
                      !alert?.is_read ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => handleQuickAction(alert)}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${getAlertColor(alert?.severity)}15` }}
                      >
                        <Icon
                          name={getAlertIcon(alert?.alert_type)}
                          size={20}
                          color={getAlertColor(alert?.severity)}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="text-sm font-interface font-semibold text-foreground">
                            {alert?.title}
                          </h4>
                          {!alert?.is_read && (
                            <button
                              onClick={(e) => {
                                e?.stopPropagation();
                                handleMarkAsRead(alert?.id);
                              }}
                              className="text-xs text-primary hover:underline flex-shrink-0"
                            >
                              Mark read
                            </button>
                          )}
                        </div>
                        <p className="text-xs font-interface text-muted-foreground mb-2">
                          {alert?.message}
                        </p>
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-interface font-medium ${
                              getSeverityBadgeColor(alert?.severity)
                            }`}
                          >
                            {alert?.severity}
                          </span>
                          <span className="text-xs font-interface text-muted-foreground">
                            {new Date(alert?.created_at)?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertDropdown;